import * as THREE from "three";

import type { AudioFeatures } from "../audio/types";
import type { AltSceneApi, CameraPersonality } from "./altScenes";
import type { BioclastParams, QualityTier } from "./scenes/bioclast";

export type LiveMathParticleMode =
  | "radialPulse"
  | "superform"
  | "spiralVortex"
  | "hopfFibration"
  | "fibonacciSequence"
  | "curlPlasma"
  | "hopfCathedral"
  | "knotChoir"
  | "lorenzEmber"
  | "phyllotacticGalaxy"
  | "reactionSkin"
  | "sphericalHarmonicShell"
  | "songPlanetCore";

const MODE_INDEX: Record<LiveMathParticleMode, number> = {
  radialPulse: 0,
  superform: 1,
  spiralVortex: 2,
  hopfFibration: 3,
  fibonacciSequence: 4,
  curlPlasma: 5,
  hopfCathedral: 6,
  knotChoir: 7,
  lorenzEmber: 8,
  phyllotacticGalaxy: 9,
  reactionSkin: 10,
  sphericalHarmonicShell: 11,
  songPlanetCore: 12,
};

const MAX_PARTICLES = 120_000;

type UniformMap = Record<string, THREE.IUniform<number>> & {
  uTime: THREE.IUniform<number>;
  uCount: THREE.IUniform<number>;
  uMode: THREE.IUniform<number>;
  uDensityPointScale: THREE.IUniform<number>;
  uDensityAlphaScale: THREE.IUniform<number>;
  uPathClarity: THREE.IUniform<number>;
  uPlanarDisk: THREE.IUniform<number>;
  uSurfaceSheet: THREE.IUniform<number>;
  uThreadStructure: THREE.IUniform<number>;
  uPlanetBody: THREE.IUniform<number>;
  uHue: THREE.IUniform<number>;
  uIntensity: THREE.IUniform<number>;
  uExposure: THREE.IUniform<number>;
  uZoomFade: THREE.IUniform<number>;
  uOpacity: THREE.IUniform<number>;
  uPointSize: THREE.IUniform<number>;
  uAmplitude: THREE.IUniform<number>;
  uFrequency: THREE.IUniform<number>;
  uRotation: THREE.IUniform<number>;
  uDepthScale: THREE.IUniform<number>;
  uWarmth: THREE.IUniform<number>;
  uBass: THREE.IUniform<number>;
  uMid: THREE.IUniform<number>;
  uTreble: THREE.IUniform<number>;
  uBeat: THREE.IUniform<number>;
  uFlow: THREE.IUniform<number>;
  uQuietBoost: THREE.IUniform<number>;
};

type MathGeometryFamily =
  | "plasma"
  | "galaxyDisk"
  | "hopfFibers"
  | "knotChoir"
  | "lorenzEmber"
  | "harmonicShell"
  | "reactionMembrane"
  | "songPlanet";

type DensitySettings = {
  pointScale: number;
  alphaScale: number;
  pathClarity: number;
};

function hashUnit(value: number): number {
  const result = Math.sin(value) * 43758.5453123;
  return result - Math.floor(result);
}

function smoothBeat(prev: number, beat: number, dt: number): number {
  if (beat > prev) return beat;
  return prev * Math.exp(-dt * 5);
}

function hsl(h: number, s: number, l: number): THREE.Color {
  const color = new THREE.Color();
  color.setHSL(((h % 360) + 360) % 360 / 360, s, l);
  return color;
}

function qualityCap(tier: QualityTier): number {
  switch (tier) {
    case "mobile": return 30_000;
    case "standard": return 60_000;
    case "high": return 85_000;
    case "ultra": return 110_000;
    case "lab": return MAX_PARTICLES;
  }
}

function familyForMode(mode: LiveMathParticleMode): MathGeometryFamily {
  switch (mode) {
    case "phyllotacticGalaxy": return "galaxyDisk";
    case "hopfFibration":
    case "hopfCathedral": return "hopfFibers";
    case "knotChoir": return "knotChoir";
    case "lorenzEmber": return "lorenzEmber";
    case "reactionSkin": return "reactionMembrane";
    case "sphericalHarmonicShell": return "harmonicShell";
    case "songPlanetCore": return "songPlanet";
    default: return "plasma";
  }
}

function densitySettingsFor(count: number, family: MathGeometryFamily): DensitySettings {
  const density = THREE.MathUtils.clamp(count / 80_000, 0.25, 1.5);
  const base: DensitySettings = {
    pointScale: THREE.MathUtils.clamp(1.08 / Math.sqrt(density), 0.64, 1.38),
    alphaScale: THREE.MathUtils.clamp(0.82 / Math.pow(density, 0.28), 0.54, 1.0),
    pathClarity: THREE.MathUtils.clamp(0.28 + density * 0.42, 0.32, 0.9),
  };
  if (family === "hopfFibers" || family === "knotChoir") {
    return { pointScale: base.pointScale * 1.0, alphaScale: base.alphaScale * 0.84, pathClarity: Math.max(base.pathClarity, 0.74) };
  }
  if (family === "galaxyDisk") {
    return { pointScale: base.pointScale * 0.9, alphaScale: base.alphaScale * 0.68, pathClarity: Math.max(base.pathClarity, 0.66) };
  }
  if (family === "reactionMembrane" || family === "harmonicShell" || family === "songPlanet") {
    return { pointScale: base.pointScale * 0.92, alphaScale: base.alphaScale * 0.76, pathClarity: Math.max(base.pathClarity, 0.58) };
  }
  if (family === "lorenzEmber") {
    return { pointScale: base.pointScale * 1.02, alphaScale: base.alphaScale * 0.72, pathClarity: Math.max(base.pathClarity, 0.7) };
  }
  return base;
}

function overlayLineMaterial(color: THREE.ColorRepresentation, opacity: number): THREE.LineBasicMaterial {
  const material = new THREE.LineBasicMaterial({
    color,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  material.userData.baseOpacity = opacity;
  return material;
}

function overlayMeshMaterial(color: THREE.ColorRepresentation, opacity: number, wireframe = false): THREE.MeshBasicMaterial {
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    wireframe,
    blending: THREE.AdditiveBlending,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
  material.userData.baseOpacity = opacity;
  return material;
}

function overlayRibbonMaterial(color: THREE.ColorRepresentation, opacity: number): THREE.MeshBasicMaterial {
  const material = overlayMeshMaterial(color, opacity);
  material.depthTest = false;
  return material;
}

function addLine(group: THREE.Group, points: THREE.Vector3[], color: THREE.ColorRepresentation, opacity: number): void {
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(geometry, overlayLineMaterial(color, opacity));
  line.frustumCulled = false;
  group.add(line);
}

function addTube(
  group: THREE.Group,
  points: THREE.Vector3[],
  color: THREE.ColorRepresentation,
  opacity: number,
  radius: number,
  closed = false,
): void {
  if (points.length < 4) return;
  const curve = new THREE.CatmullRomCurve3(points, closed, "centripetal", 0.35);
  const geometry = new THREE.TubeGeometry(curve, Math.max(48, points.length * 2), radius, 6, closed);
  const tube = new THREE.Mesh(geometry, overlayRibbonMaterial(color, opacity));
  tube.frustumCulled = false;
  group.add(tube);
}

function addClosedLine(group: THREE.Group, points: THREE.Vector3[], color: THREE.ColorRepresentation, opacity: number): void {
  if (points.length > 1) points.push(points[0].clone());
  addLine(group, points, color, opacity);
}

function disposeObjectTree(root: THREE.Object3D): void {
  root.traverse((object) => {
    const mesh = object as THREE.Mesh | THREE.Line | THREE.Points;
    mesh.geometry?.dispose();
    const material = mesh.material;
    if (Array.isArray(material)) material.forEach((entry) => entry.dispose());
    else material?.dispose();
  });
}

function addHopfOverlay(group: THREE.Group): void {
  const fiberCount = 20;
  const sampleCount = 112;
  for (let fiberIndex = 0; fiberIndex < fiberCount; fiberIndex++) {
    const latitude = fiberIndex % 5;
    const longitude = Math.floor(fiberIndex / 5);
    const c = Math.sin(THREE.MathUtils.lerp(-1.0, 1.0, (latitude + 0.5) / 5)) * 0.72;
    const basePhi = ((longitude + 0.5) / 4) * Math.PI * 2 + latitude * 0.28;
    const baseRadius = Math.sqrt(Math.max(0, 1 - c * c));
    const a = baseRadius * Math.cos(basePhi);
    const b = baseRadius * Math.sin(basePhi);
    const xi = Math.atan2(b, a);
    const points: THREE.Vector3[] = [];
    for (let sampleIndex = 0; sampleIndex <= sampleCount; sampleIndex++) {
      const s = sampleIndex / sampleCount;
      const angle = s * Math.PI * 2 + latitude * 0.22;
      const c1 = Math.sqrt(0.5 * (1 + c));
      const c2 = Math.sqrt(0.5 * (1 - c));
      const a1 = (angle + xi) * 0.5;
      const a2 = (angle - xi) * 0.5;
      const x4 = c1 * Math.cos(a1);
      const y4 = c1 * Math.sin(a1);
      const z4 = c2 * Math.cos(a2);
      const w4 = c2 * Math.sin(a2);
      const denom = Math.max(1 - w4, 0.34);
      const point = new THREE.Vector3(x4, y4 * 1.38, z4).multiplyScalar(15.5 / denom);
      point.applyAxisAngle(new THREE.Vector3(1, 0, 0), 0.42);
      points.push(point);
    }
    const color = fiberIndex % 2 ? 0x7ee7ff : 0xffd57a;
    addLine(group, points, color, 0.09);
    addTube(group, points, color, 0.038, 0.026, false);
  }
}

function addKnotOverlay(group: THREE.Group): void {
  const sampleCount = 192;
  for (let knotIndex = 0; knotIndex < 9; knotIndex++) {
    const lane = knotIndex / 8;
    const pK = 2 + (knotIndex % 3);
    const qK = 3 + Math.floor(knotIndex / 3);
    const major = 10.4 + lane * 5.6;
    const minor = 3.0;
    const points: THREE.Vector3[] = [];
    for (let sampleIndex = 0; sampleIndex <= sampleCount; sampleIndex++) {
      const u = (sampleIndex / sampleCount) * Math.PI * 2;
      points.push(new THREE.Vector3(
        (major + minor * Math.cos(qK * u)) * Math.cos(pK * u),
        minor * Math.sin(qK * u) * 1.1 + (lane - 0.5) * 2.2,
        (major + minor * Math.cos(qK * u)) * Math.sin(pK * u),
      ));
    }
    const color = knotIndex % 2 ? 0xffd982 : 0xd6a7ff;
    addLine(group, points, color, 0.1);
    addTube(group, points, color, 0.045, 0.03, true);
  }
}

function addGalaxyOverlay(group: THREE.Group): void {
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(35, 128),
    overlayMeshMaterial(0xd9a85f, 0.045),
  );
  disc.rotation.x = -Math.PI / 2;
  group.add(disc);
  for (let armIndex = 0; armIndex < 4; armIndex++) {
    const points: THREE.Vector3[] = [];
    for (let sampleIndex = 0; sampleIndex < 180; sampleIndex++) {
      const t = sampleIndex / 179;
      const radius = 2 + 33 * Math.sqrt(t);
      const theta = armIndex * Math.PI * 0.5 + t * Math.PI * 5.2;
      const wave = Math.sin(t * Math.PI * 11 + armIndex) * 0.45;
      points.push(new THREE.Vector3(Math.cos(theta) * (radius + wave), wave * 0.8, Math.sin(theta) * (radius + wave)));
    }
    const color = armIndex % 2 ? 0x75d8ff : 0xffd276;
    addLine(group, points, color, 0.09);
    addTube(group, points, color, 0.032, 0.045, false);
  }
}

function addLorenzOverlay(group: THREE.Group): void {
  for (let sideIndex = 0; sideIndex < 2; sideIndex++) {
    const side = sideIndex === 0 ? -1 : 1;
    const points: THREE.Vector3[] = [];
    for (let sampleIndex = 0; sampleIndex < 180; sampleIndex++) {
      const u = (sampleIndex / 179) * Math.PI * 2;
      const lobe = Math.sin(u);
      const wing = 7.4 + 4.8 * Math.pow(Math.abs(lobe), 0.72);
      points.push(new THREE.Vector3(
        side * wing + Math.sin(u * 2) * 2.4,
        Math.cos(u * 1.35) * 6.2 + Math.sin(u * 3) * 1.5,
        Math.sin(u * 2.15 + side * 0.7) * 6.4,
      ));
    }
    const color = sideIndex === 0 ? 0xff7a45 : 0xffc05a;
    addClosedLine(group, points, color, 0.09);
    addTube(group, points, color, 0.04, 0.034, true);
  }
}

function addSphereOverlay(group: THREE.Group, family: MathGeometryFamily): void {
  const color = family === "reactionMembrane" ? 0x53ffd1 : family === "songPlanet" ? 0x71e8ff : 0x9eefff;
  const opacity = family === "songPlanet" ? 0.075 : 0.064;
  const radius = family === "songPlanet" ? 16.2 : family === "reactionMembrane" ? 18 : 17.2;
  const shell = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 64, 32),
    overlayMeshMaterial(color, opacity, family !== "songPlanet"),
  );
  group.add(shell);
  if (family === "songPlanet") {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(24, 0.055, 8, 160),
      overlayMeshMaterial(0xbda6ff, 0.14),
    );
    ring.rotation.x = 0.42;
    group.add(ring);
  }
}

function addPlasmaOverlay(group: THREE.Group): void {
  group.add(new THREE.Mesh(
    new THREE.IcosahedronGeometry(18, 3),
    overlayMeshMaterial(0x65e7ff, 0.052, true),
  ));
}

function createStructureOverlay(mode: LiveMathParticleMode): THREE.Group {
  const group = new THREE.Group();
  const family = familyForMode(mode);
  if (family === "hopfFibers") addHopfOverlay(group);
  else if (family === "knotChoir") addKnotOverlay(group);
  else if (family === "galaxyDisk") addGalaxyOverlay(group);
  else if (family === "lorenzEmber") addLorenzOverlay(group);
  else if (family === "reactionMembrane" || family === "harmonicShell" || family === "songPlanet") addSphereOverlay(group, family);
  else addPlasmaOverlay(group);
  group.renderOrder = 1;
  return group;
}

const VERTEX_SHADER = `
  precision highp float;
  attribute float aIndex;
  attribute vec2 aSeed;
  uniform float uTime;
  uniform float uCount;
  uniform float uMode;
  uniform float uDensityPointScale;
  uniform float uDensityAlphaScale;
  uniform float uPathClarity;
  uniform float uPlanarDisk;
  uniform float uSurfaceSheet;
  uniform float uThreadStructure;
  uniform float uPlanetBody;
  uniform float uHue;
  uniform float uIntensity;
  uniform float uExposure;
  uniform float uZoomFade;
  uniform float uOpacity;
  uniform float uPointSize;
  uniform float uAmplitude;
  uniform float uFrequency;
  uniform float uRotation;
  uniform float uDepthScale;
  uniform float uWarmth;
  uniform float uBass;
  uniform float uMid;
  uniform float uTreble;
  uniform float uBeat;
  uniform float uFlow;
  uniform float uQuietBoost;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vEnergy;

  const float TAU = 6.28318530718;
  const float PI = 3.14159265359;
  const float GOLDEN_ANGLE = 2.39996322973;

  vec3 hsl2rgb(vec3 c) {
    vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
    return c.z + c.y * (rgb - 0.5) * (1.0 - abs(2.0 * c.z - 1.0));
  }

  vec3 referencePalette(float x, float energy, float warmthBias) {
    float warmth = clamp(uWarmth + warmthBias, 0.0, 1.0);
    vec3 cool = hsl2rgb(vec3(0.55, 0.45, 0.55));
    vec3 amber = hsl2rgb(vec3(0.10, 0.65, 0.55));
    vec3 hot = hsl2rgb(vec3(0.05, 0.78, 0.62));
    vec3 magenta = hsl2rgb(vec3(0.92, 0.70, 0.55));
    vec3 base = mix(cool, amber, smoothstep(0.18, 0.78, warmth));
    vec3 hi = mix(amber, hot, clamp(energy * 0.6, 0.0, 1.0));
    vec3 color = mix(base, hi, smoothstep(0.32, 0.92, x));
    return mix(color, magenta, smoothstep(0.55, 0.95, energy) * 0.18);
  }

  mat2 rot(float a) {
    float s = sin(a);
    float c = cos(a);
    return mat2(c, -s, s, c);
  }

  vec3 rotateY(vec3 p, float a) {
    p.xz = rot(a) * p.xz;
    return p;
  }

  vec3 rotateX(vec3 p, float a) {
    p.yz = rot(a) * p.yz;
    return p;
  }

  float superRadius(float a, float m, float n1, float n2, float n3) {
    float c = abs(cos(m * a * 0.25));
    float s = abs(sin(m * a * 0.25));
    return pow(max(0.0001, pow(c, n2) + pow(s, n3)), -1.0 / max(0.15, n1));
  }

  vec4 radialPulse(float t, vec2 seed) {
    float y0 = 1.0 - 2.0 * t;
    float r0 = sqrt(max(0.0, 1.0 - y0 * y0));
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.012 + uRotation * 0.05);

    float topMask = smoothstep(0.0, 1.0, y0);
    float bowlMask = 1.0 - smoothstep(-1.0, 0.0, y0);
    float coneRadius = mix(1.0, 0.04, topMask);
    float bowlRadius = mix(1.0, 1.05, bowlMask);
    float radialScale = coneRadius * bowlRadius;

    float stemPull = pow(topMask, 2.0) * 0.55;
    float yScale = mix(0.85, 0.85 + stemPull * 1.4, 1.0);

    float rd = 1.0 - r0;
    float ringPhase = rd * TAU * (uFrequency * 1.6) - uTime * (0.85 + uFlow * 0.4);
    float ring = sin(ringPhase + uBass * 1.2);

    float radius = 22.0;
    vec3 p;
    p.x = cos(theta) * r0 * radius * radialScale;
    p.z = sin(theta) * r0 * radius * radialScale;
    p.y = y0 * radius * yScale;

    p.xz *= 1.0 + ring * uAmplitude * 0.14 + uBeat * 0.06;
    p.y += ring * uAmplitude * 2.0 * (0.4 + bowlMask * 0.6);

    return vec4(p, topMask);
  }

  vec4 superform(float t, vec2 seed) {
    float yBase = 1.0 - 2.0 * t;
    float phi0 = acos(clamp(yBase, -0.98, 0.98));
    float adv = uTime * (0.20 + uFlow * 0.18 + uRotation * 0.12);
    float lane = seed.x * TAU;

    float phi = clamp(
      phi0 + 0.22 * sin(adv * 0.55 + lane + t * TAU),
      0.035,
      PI - 0.035
    );
    float theta = aIndex * GOLDEN_ANGLE
                + adv * (0.75 + seed.y * 0.45)
                + 0.22 * sin(phi * 3.0 - adv * 0.8 + lane);

    float m = 4.0 + uFrequency * 1.35 + 0.45 * sin(uTime * 0.13 + seed.y * TAU);
    float n1 = 0.92 + uAmplitude * 0.42 + 0.08 * sin(uTime * 0.17 + seed.x * TAU);
    float n2 = 1.45 + 0.25 * sin(uTime * 0.11 + seed.y * 5.0);
    float n3 = 1.45 + 0.25 * cos(uTime * 0.10 + seed.x * 4.0);

    float gAz = superRadius(theta, m, n1, n2, n3);
    float gLat = superRadius(phi - PI * 0.5, 2.0 + uFrequency * 0.18, n1, 1.6, 1.6);
    float shape = mix(1.0, gAz * gLat, clamp(uAmplitude * 0.38, 0.0, 0.48));

    float stream = sin(theta * 2.0 + phi * 5.0 - uTime * (1.10 + uFlow * 0.65) + lane);
    float fine = sin(theta * 7.0 - phi * 2.0 - uTime * 1.7 + seed.y * TAU);

    float r0 = sin(phi);
    float y0 = cos(phi);
    float radius = 21.5 * shape;
    vec3 p = vec3(cos(theta) * r0, y0, sin(theta) * r0) * radius;

    vec3 eTheta = normalize(vec3(-sin(theta), 0.0, cos(theta)));
    vec3 ePhi = normalize(vec3(cos(theta) * cos(phi), -sin(phi), sin(theta) * cos(phi)));
    p += eTheta * stream * uAmplitude * 0.85;
    p += ePhi * fine * uAmplitude * 0.35;
    p *= 1.0 + uBeat * 0.035;

    float band = 0.5 + 0.5 * stream;
    return vec4(p, band);
  }

  vec4 spiralVortex(float t, vec2 seed) {
    float bucket = mod(aIndex, 8.0);
    float subtype = step(7.0, bucket);

    if (subtype > 0.5) {
      float innerCount = max(1.0, floor(uCount / 8.0));
      float innerIdx = floor(aIndex / 8.0);
      float u = (innerIdx / innerCount) * TAU + uTime * (0.05 + uRotation * 0.08);
      float v = seed.y * TAU;
      float majorRadius = 8.0;
      float minorRadius = 1.3 + uAmplitude * 0.3 + uBeat * 0.20;
      vec3 p;
      p.x = (majorRadius + minorRadius * cos(v)) * cos(u);
      p.z = (majorRadius + minorRadius * cos(v)) * sin(u);
      p.y = minorRadius * sin(v) * 0.45;
      return vec4(p, 1.0);
    } else {
      float cols = 280.0;
      float outerCount = uCount * 0.875;
      float rowsF = max(2.0, floor(outerCount / cols));
      float col = mod(aIndex, cols);
      float row = floor(aIndex / cols);
      float u = (col + seed.x * 0.5) / cols * TAU + uTime * (0.04 + uRotation * 0.06);
      float v = (row + seed.y * 0.5) / rowsF * TAU;

      float majorRadius = 14.0;
      float minorRadius = 7.5 + uAmplitude * 0.6;

      float lift = sin(u * 3.0 + uTime * 0.4) * (0.6 + uAmplitude * 0.5);
      float twist = sin(v) * (0.4 + uFrequency * 0.15);

      vec3 p;
      p.x = (majorRadius + minorRadius * cos(v)) * cos(u + twist * 0.04);
      p.z = (majorRadius + minorRadius * cos(v)) * sin(u + twist * 0.04);
      p.y = minorRadius * sin(v) + lift;

      return vec4(p, 0.0);
    }
  }

  vec4 hopfFibration(float t, vec2 seed) {
    float fiberCount = 64.0;
    float fiberLen = max(96.0, floor(uCount / fiberCount));
    float fiberIdx = mod(floor(aIndex / fiberLen), fiberCount);
    float along = mod(aIndex, fiberLen);
    float s = (along + 0.5) / fiberLen;

    float latBands = 8.0;
    float lonBands = max(3.0, floor(fiberCount / latBands));
    float latIdx = mod(fiberIdx, latBands);
    float lonIdx = floor(fiberIdx / latBands);
    float latT = (latIdx + 0.5) / latBands;
    float lonT = (lonIdx + 0.5) / lonBands;

    float c0 = sin(mix(-1.05, 1.02, latT)) * 0.78;
    float c = clamp(c0 + 0.028 * sin(uTime * 0.10 + lonIdx * 0.37), -0.84, 0.80);
    float bphi = lonT * TAU
               + latIdx * GOLDEN_ANGLE * 0.23
               + uTime * (0.030 + uFlow * 0.014)
               + 0.045 * sin(uTime * 0.08 + latIdx * 0.9);
    float bs = sqrt(max(0.0, 1.0 - c * c));
    float a = bs * cos(bphi);
    float b = bs * sin(bphi);

    float xi = atan(b, a);

    float streamSpeed = 0.55 + uFlow * 0.34 + uRotation * 0.20;
    float ang = s * TAU
              + uTime * streamSpeed
              + latIdx * 0.19
              + lonIdx * 0.047
              + 0.06 * sin(uTime * 0.14 + lonIdx * 0.31);

    float c1 = sqrt(0.5 * (1.0 + c));
    float c2 = sqrt(0.5 * (1.0 - c));
    float a1 = (ang + xi) * 0.5;
    float a2 = (ang - xi) * 0.5;

    float x4 = c1 * cos(a1);
    float y4 = c1 * sin(a1);
    float z4 = c2 * cos(a2);
    float w4 = c2 * sin(a2);

    float rawDenom = 1.0 - w4;
    float visibility = smoothstep(0.36, 0.86, rawDenom);
    float denom = max(rawDenom, 0.30);
    vec3 p = vec3(x4, y4, z4) * (18.0 / denom);

    float mag = length(p);
    float cap = 58.0;
    if (mag > cap) p *= cap / mag;

    p = rotateX(p, 0.34);
    p = rotateY(p, -0.28);
    p *= 1.0 + uBeat * 0.025;

    float stripe = pow(0.5 + 0.5 * sin(s * TAU * 5.0 - uTime * (2.4 + uFlow * 0.9) + lonIdx * 0.41), 5.0);
    float family = 0.5 + 0.5 * sin(latIdx * 0.75 + lonIdx * 0.23);
    float subtype = visibility * (0.34 + 0.18 * family + 0.34 * stripe);
    return vec4(p, subtype);
  }

  vec4 fibonacciSequence(float t, vec2 seed) {
    float armCount = 34.0;
    float armIdx = mod(aIndex, armCount);
    float stepIdx = floor(aIndex / armCount);
    float steps = max(1.0, floor(uCount / armCount));
    float drift = uTime * (8.0 + uFlow * 18.0 + uRotation * 8.0);
    float q = fract((stepIdx + drift + armIdx * 0.21) / steps);
    float theta = armIdx * GOLDEN_ANGLE
                + q * TAU * (2.35 + uFrequency * 0.42)
                + q * q * (1.15 + uDepthScale * 0.55);

    float sqrtQ = sqrt(q);
    float radius = 1.8 + 30.0 * sqrtQ;
    float armWave = sin(armIdx * 0.62 + q * TAU * (5.0 + uFrequency * 0.8) - uTime * (0.8 + uFlow * 0.5));
    float shell = log(1.0 + q * 18.0);

    vec3 p;
    p.x = cos(theta) * radius * (1.0 + armWave * uAmplitude * 0.035);
    p.z = sin(theta) * radius * (1.0 + armWave * uAmplitude * 0.035);
    p.y = (q - 0.5) * (3.8 + uDepthScale * 6.8)
        + sin(theta * 0.13 - uTime * 1.2) * (0.7 + uAmplitude * 0.9);

    float curl = smoothstep(0.16, 1.0, q);
    p.yz = rot((curl - 0.5) * (0.42 + uDepthScale * 0.36)) * p.yz;
    p.xz = rot(shell * 0.26 + uTime * (0.035 + uRotation * 0.08)) * p.xz;

    float rib = pow(0.5 + 0.5 * sin(q * TAU * 13.0 + armIdx * GOLDEN_ANGLE - uTime * (1.8 + uFlow)), 3.2);
    rib = max(rib, pow(0.5 + 0.5 * sin(q * TAU * 21.0 - armIdx * 0.74 + uTime * 1.1), 5.0) * 0.72);
    p *= 1.0 + uBeat * 0.035 + rib * uAmplitude * 0.018;

    return vec4(p, rib);
  }

  vec4 curlPlasma(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.10 + uRotation * 0.18);
    float shell = pow(seed.x, 0.42);
    float sphereR = sqrt(max(0.0, 1.0 - y * y));
    vec3 p = vec3(cos(theta) * sphereR, y, sin(theta) * sphereR) * (7.0 + shell * 15.5);

    float curlA = sin(p.y * (0.28 + uFrequency * 0.035) + uTime * (0.62 + uFlow * 0.38) + seed.y * TAU);
    float curlB = cos(length(p.xz) * 0.16 - uTime * (0.78 + uFlow * 0.42) + seed.x * 7.0);
    p.xz = rot((curlA + curlB) * (0.42 + uAmplitude * 0.68 + uBass * 0.18)) * p.xz;
    p.y += (curlA - curlB) * (1.6 + uAmplitude * 2.2) + uBeat * (seed.x - 0.5) * 3.4;
    p *= 1.0 + uBass * 0.035 + uBeat * 0.025;

    float filament = smoothstep(0.42, 1.0, abs(curlA * curlB));
    return vec4(p, filament);
  }

  vec4 hopfCathedral(float t, vec2 seed) {
    float fiberCount = 84.0;
    float fiberLen = max(80.0, floor(uCount / fiberCount));
    float fiberIdx = mod(floor(aIndex / fiberLen), fiberCount);
    float along = mod(aIndex, fiberLen);
    float s = (along + 0.5) / fiberLen;

    float latBands = 7.0;
    float lonBands = max(3.0, floor(fiberCount / latBands));
    float latIdx = mod(fiberIdx, latBands);
    float lonIdx = floor(fiberIdx / latBands);
    float c = sin(mix(-1.0, 1.0, (latIdx + 0.5) / latBands)) * 0.72;
    float basePhi = ((lonIdx + 0.5) / lonBands) * TAU + latIdx * 0.28 + uTime * (0.022 + uFlow * 0.012);
    float bs = sqrt(max(0.0, 1.0 - c * c));
    float a = bs * cos(basePhi);
    float b = bs * sin(basePhi);
    float xi = atan(b, a);

    float ang = s * TAU + uTime * (0.38 + uFlow * 0.22 + uRotation * 0.12) + latIdx * 0.22;
    float c1 = sqrt(0.5 * (1.0 + c));
    float c2 = sqrt(0.5 * (1.0 - c));
    float a1 = (ang + xi) * 0.5;
    float a2 = (ang - xi) * 0.5;
    float x4 = c1 * cos(a1);
    float y4 = c1 * sin(a1);
    float z4 = c2 * cos(a2);
    float w4 = c2 * sin(a2);
    float denom = max(1.0 - w4, 0.34);
    vec3 p = vec3(x4, y4, z4) * (15.5 / denom);
    p.y *= 1.38 + uDepthScale * 0.22;
    p.xz *= 0.88 + uBass * 0.05;
    p = rotateX(p, 0.42);

    float arch = pow(sin(s * PI), 0.55);
    float lane = pow(0.5 + 0.5 * sin(s * TAU * (4.0 + uFrequency * 0.45) - uTime * (1.8 + uFlow)), 4.0);
    p.y += arch * (1.4 + uAmplitude * 1.8) + uBeat * lane * 1.2;
    return vec4(p, 0.26 + lane * 0.62 + arch * 0.12);
  }

  vec4 knotChoir(float t, vec2 seed) {
    float knotCount = 9.0;
    float knotLen = max(96.0, floor(uCount / knotCount));
    float knotIdx = mod(floor(aIndex / knotLen), knotCount);
    float along = mod(aIndex, knotLen);
    float s = (along + 0.5) / knotLen;
    float lane = knotIdx / max(1.0, knotCount - 1.0);
    float pK = 2.0 + mod(knotIdx, 3.0);
    float qK = 3.0 + mod(floor(knotIdx / 3.0), 3.0);
    float u = s * TAU + uTime * (0.18 + uFlow * 0.12 + seed.x * 0.02);
    float major = 10.4 + lane * 5.6 + uBass * 0.9;
    float minor = 2.8 + uAmplitude * 1.1 + uBeat * 0.35;
    float tube = seed.y * TAU;

    vec3 core;
    core.x = (major + minor * cos(qK * u)) * cos(pK * u);
    core.z = (major + minor * cos(qK * u)) * sin(pK * u);
    core.y = minor * sin(qK * u) * (1.1 + uDepthScale * 0.28) + (lane - 0.5) * 2.2;

    vec3 radial = normalize(vec3(core.x, 0.0, core.z) + vec3(0.001, 0.0, 0.001));
    vec3 binormal = normalize(cross(radial, vec3(0.0, 1.0, 0.0)) + vec3(0.001));
    core += radial * cos(tube) * (0.34 + seed.x * 0.34) + binormal * sin(tube) * (0.34 + seed.x * 0.26);
    core = rotateY(core, uTime * (0.020 + uRotation * 0.09));

    float stripe = pow(0.5 + 0.5 * sin(s * TAU * (5.0 + qK) - uTime * (2.0 + uFlow)), 5.0);
    return vec4(core, stripe * 0.8 + lane * 0.2);
  }

  vec4 lorenzEmber(float t, vec2 seed) {
    float strandCount = 42.0;
    float strandLen = max(64.0, floor(uCount / strandCount));
    float strand = mod(floor(aIndex / strandLen), strandCount);
    float along = fract((mod(aIndex, strandLen) + uTime * (12.0 + uFlow * 22.0 + seed.x * 2.0)) / strandLen);
    float side = mix(-1.0, 1.0, step(0.5, fract(strand * 0.618 + seed.x)));
    float u = along * TAU;
    float lobe = sin(u);
    float wing = 7.4 + 4.8 * pow(abs(lobe), 0.72) + uBass * 1.0;

    vec3 p;
    p.x = side * wing + sin(u * 2.0 + strand * 0.17) * (2.4 + uAmplitude * 1.1);
    p.y = cos(u * 1.35 + strand * 0.09) * (6.2 + uDepthScale * 2.2) + sin(u * 3.0) * 1.5;
    p.z = sin(u * 2.15 + side * 0.7) * (6.4 + uAmplitude * 2.6) + (seed.y - 0.5) * 2.5;
    p.xz = rot(uTime * (0.06 + uRotation * 0.1) + side * 0.08) * p.xz;
    p *= 1.0 + uBeat * 0.028;

    float ember = pow(1.0 - along, 1.7) * 0.55 + pow(abs(lobe), 4.0) * 0.45;
    return vec4(p, ember);
  }

  vec4 phyllotacticGalaxy(float t, vec2 seed) {
    float q = t;
    float arm = mod(aIndex, 55.0);
    float theta = aIndex * GOLDEN_ANGLE + q * TAU * (1.25 + uFrequency * 0.24) + uTime * (0.055 + uFlow * 0.08);
    float radius = 1.5 + 34.0 * sqrt(q);
    float armWave = sin(arm * 0.42 + q * TAU * (7.0 + uFrequency) - uTime * (1.0 + uFlow));
    float dust = pow(0.5 + 0.5 * armWave, 3.0);
    vec3 p;
    p.x = cos(theta) * radius * (1.0 + dust * uAmplitude * 0.035 + uBeat * 0.018);
    p.z = sin(theta) * radius * (1.0 + dust * uAmplitude * 0.035 + uBeat * 0.018);
    p.y = (seed.y - 0.5) * (1.2 + q * 5.8 + uDepthScale * 4.2) + armWave * (0.45 + uAmplitude * 0.8 + uTreble * 0.35);
    p.xz = rot(log(1.0 + q * 14.0) * (0.22 + uDepthScale * 0.14)) * p.xz;
    return vec4(p, dust);
  }

  vec4 reactionSkin(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.018 + uRotation * 0.05);
    float r = sqrt(max(0.0, 1.0 - y * y));
    vec3 n = normalize(vec3(cos(theta) * r, y, sin(theta) * r));
    float phi = atan(n.z, n.x);
    float cellA = sin(phi * (7.0 + uFrequency * 0.8) + sin(n.y * 8.0 + uTime * 0.55) * 1.8);
    float cellB = cos(n.y * (11.0 + uFrequency) - uTime * (0.75 + uFlow * 0.4) + seed.x * TAU);
    float cells = cellA * cellB;
    float edge = smoothstep(0.52, 0.94, abs(cells));
    float radius = 18.0 * (1.0 + edge * (0.045 + uAmplitude * 0.12) + uBeat * 0.02);
    vec3 p = n * radius;
    p += normalize(vec3(-n.z, 0.0, n.x) + vec3(0.001)) * sin(cells * 3.0 + uTime) * (0.35 + uTreble * 0.5);
    return vec4(p, edge);
  }

  vec4 sphericalHarmonicShell(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float theta = aIndex * GOLDEN_ANGLE;
    float r = sqrt(max(0.0, 1.0 - y * y));
    vec3 n = normalize(vec3(cos(theta) * r, y, sin(theta) * r));
    float phi = atan(n.z, n.x);
    float polar = acos(clamp(n.y, -1.0, 1.0));
    float lobesA = sin(polar * (3.0 + floor(uFrequency))) * cos(phi * (5.0 + floor(uFrequency * 0.7)) + uTime * 0.18);
    float lobesB = cos(polar * 4.0 - uTime * 0.22) * sin(phi * 7.0 + uTime * 0.13);
    float lobe = lobesA * 0.72 + lobesB * 0.28;
    float ridge = smoothstep(0.42, 0.92, abs(lobe));
    float radius = 17.2 * (1.0 + lobe * (0.13 + uAmplitude * 0.22 + uBass * 0.04) + uBeat * 0.018);
    vec3 p = n * radius;
    p = rotateY(p, uTime * (0.026 + uRotation * 0.11));
    return vec4(p, ridge);
  }

  vec4 songPlanetCore(float t, vec2 seed) {
    float bucket = mod(aIndex, 11.0);
    if (bucket > 9.0) {
      float ringCount = max(1.0, floor(uCount / 11.0));
      float ringIndex = floor(aIndex / 11.0);
      float q = fract(ringIndex / ringCount + uTime * (0.018 + uFlow * 0.025));
      float theta = q * TAU * (2.0 + uFrequency * 0.22) + seed.x * 0.5;
      float orbitRadius = 22.0 + seed.y * 9.0 + uBeat * 1.2;
      vec3 p = vec3(cos(theta) * orbitRadius, sin(theta * 1.7 + seed.y * TAU) * 2.0, sin(theta) * orbitRadius);
      p = rotateX(p, 0.42 + uDepthScale * 0.22);
      return vec4(p, 1.0);
    }

    float y = 1.0 - 2.0 * t;
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.012 + uRotation * 0.045);
    float r = sqrt(max(0.0, 1.0 - y * y));
    vec3 n = normalize(vec3(cos(theta) * r, y, sin(theta) * r));
    float phi = atan(n.z, n.x);
    float terrain = sin(phi * 5.0 + y * 7.0 + uTime * 0.25) * cos(y * 9.0 - uTime * 0.16);
    float bands = pow(0.5 + 0.5 * sin(y * 18.0 + terrain * 1.6 + uTime * (0.32 + uFlow * 0.25)), 4.0);
    float radius = 16.2 * (1.0 + terrain * (0.045 + uAmplitude * 0.08) + bands * uBeat * 0.025);
    return vec4(n * radius, bands);
  }

  vec4 sampleMathMode(float mode, float t, vec2 seed) {
    float m = mode;
    if (m < 0.5) {
      return radialPulse(t, seed);
    } else if (m < 1.5) {
      return superform(t, seed);
    } else if (m < 2.5) {
      return spiralVortex(t, seed);
    } else if (m < 3.5) {
      return hopfFibration(t, seed);
    } else if (m < 4.5) {
      return fibonacciSequence(t, seed);
    } else if (m < 5.5) {
      return curlPlasma(t, seed);
    } else if (m < 6.5) {
      return hopfCathedral(t, seed);
    } else if (m < 7.5) {
      return knotChoir(t, seed);
    } else if (m < 8.5) {
      return lorenzEmber(t, seed);
    } else if (m < 9.5) {
      return phyllotacticGalaxy(t, seed);
    } else if (m < 10.5) {
      return reactionSkin(t, seed);
    } else if (m < 11.5) {
      return sphericalHarmonicShell(t, seed);
    }
    return songPlanetCore(t, seed);
  }

  void main() {
    float count = max(1.0, uCount);
    float t = clamp((aIndex + 0.5) / count, 0.0, 1.0);

    vec4 packed = sampleMathMode(uMode, t, aSeed);
    vec3 p = packed.xyz;
    float subtype = packed.w;

    float quietBoost = clamp(uQuietBoost, 0.0, 1.0);
    float preEnergy = clamp((uBass + uMid + uTreble) / 3.0 + uBeat * 0.6 + quietBoost * 0.24, 0.0, 1.5);

    vec3 n = normalize(p + vec3(0.0001));
    float micro = sin(uTime * 1.1 + aSeed.x * 41.0 + t * 23.0) * 0.18
                + sin(uTime * 0.7 + aSeed.y * 17.0 + length(p) * 0.08) * 0.12;
    p += n * micro * uAmplitude * (0.9 + preEnergy * 0.6);

    p = rotateY(p, uTime * (0.030 + uRotation * 0.13));
    p = rotateX(p, sin(uTime * 0.16) * 0.08 * uDepthScale);

    vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);

    float sizePulse = smoothstep(0.45, 0.95, preEnergy) * 0.20 + uBeat * 0.25;
    float distScale = mix(1.0, 150.0 / max(46.0, -mvPosition.z), 0.36);
    float modePointScale = 1.0;
    if (uMode >= 2.5 && uMode < 3.5) modePointScale = 0.72;
    else if (uMode >= 3.5 && uMode < 5.5) modePointScale = 0.48;
    else if (uMode >= 5.5 && uMode < 8.5) modePointScale = 0.40;
    else if (uMode >= 8.5 && uMode < 9.5) modePointScale = 0.52;
    else if (uMode >= 9.5) modePointScale = 0.48;
    if (uThreadStructure > 0.5) modePointScale *= mix(0.82, 1.06, subtype);
    if (uPlanarDisk > 0.5) modePointScale *= mix(0.74, 1.02, subtype);
    if (uPlanetBody > 0.5) modePointScale *= mix(0.86, 1.1, subtype);
    gl_PointSize = uPointSize * (1.0 + quietBoost * 0.28 + sizePulse * 0.38) * distScale * modePointScale * uDensityPointScale * mix(0.94, 1.0, uZoomFade);
    gl_Position = projectionMatrix * mvPosition;

    vec3 normal = normalize(p + vec3(0.0001));
    vec3 lightDir = normalize(vec3(0.55, 0.62, 0.55));
    float light = 0.55 + max(0.0, dot(normal, lightDir)) * 0.55;
    float rim = pow(1.0 - abs(dot(normalize(-mvPosition.xyz), normal)), 2.2);
    float grad = clamp(0.45 + p.y * 0.012 + length(p.xz) * 0.011, 0.0, 1.0);

    float warmthBias = 0.0;
    float modeVis = 1.0;
    if (uMode < 0.5) {
      warmthBias = 0.05 + subtype * 0.15;
      grad += subtype * 0.25;
    } else if (uMode < 1.5) {
      warmthBias = 0.08;
    } else if (uMode < 2.5) {
      warmthBias = subtype > 0.5 ? 0.30 : -0.45;
      grad = subtype > 0.5 ? 0.65 : 0.30;
    } else if (uMode < 3.5) {
      modeVis = clamp(0.20 + subtype * 0.95, 0.0, 1.0);
      warmthBias = 0.12 + subtype * 0.12;
      grad = 0.34 + subtype * 0.38 + aSeed.x * 0.10;
      rim *= 0.70 + 0.30 * modeVis;
      light *= 0.72 + 0.34 * modeVis;
    } else if (uMode < 5.5) {
      modeVis = 0.28 + subtype * 0.68;
      warmthBias = 0.22 + subtype * 0.16;
      grad = 0.32 + subtype * 0.55 + aSeed.y * 0.08;
      rim *= 0.85 + subtype * 0.25;
    } else if (uMode < 8.5) {
      modeVis = 0.22 + subtype * 0.82;
      warmthBias = -0.16 + subtype * 0.28;
      grad = 0.28 + subtype * 0.62 + aSeed.x * 0.12;
      light *= 0.70 + subtype * 0.44;
      rim *= 0.82 + subtype * 0.34;
    } else if (uMode < 9.5) {
      modeVis = 0.30 + subtype * 0.72;
      warmthBias = 0.36 + subtype * 0.24;
      grad = 0.34 + subtype * 0.58;
      rim *= 0.88 + subtype * 0.24;
    } else {
      modeVis = 0.30 + subtype * 0.72;
      warmthBias = 0.02 + subtype * 0.22;
      grad = 0.30 + subtype * 0.58 + aSeed.y * 0.08;
      rim *= 0.84 + subtype * 0.28;
    }

    grad = mix(grad, subtype, uPathClarity * 0.22);
    vec3 pal = referencePalette(clamp(grad, 0.0, 1.0), preEnergy, warmthBias);
    vec3 hueTint = hsl2rgb(vec3(fract(uHue + 0.0), 0.45, 0.55));
    pal = mix(pal, hueTint, 0.05);

    float modeBrightness = 1.34;
    if (uMode >= 0.5 && uMode < 1.5) modeBrightness = 1.28;
    else if (uMode >= 2.5 && uMode < 3.5) modeBrightness = 1.42;
    else if (uMode >= 3.5 && uMode < 5.5) modeBrightness = 1.36;
    else if (uMode >= 5.5 && uMode < 8.5) modeBrightness = 1.16;
    else if (uMode >= 8.5) modeBrightness = 1.26;
    float clarityDimming = 1.0 - uPathClarity * (0.12 + uThreadStructure * 0.22 + uPlanarDisk * 0.2 + uSurfaceSheet * 0.14 + uPlanetBody * 0.12);
    vec3 litColor = pal * (1.02 + light * 0.48 + rim * 0.46 + preEnergy * (0.16 + uIntensity * 0.07));
    vColor = litColor * modeBrightness * clarityDimming * uExposure * (1.0 + quietBoost * 0.22);
    float modeAlphaScale = 1.0;
    if (uMode >= 2.5 && uMode < 3.5) modeAlphaScale = 1.16;
    else if (uMode >= 3.5 && uMode < 5.5) modeAlphaScale = 1.12;
    else if (uMode >= 5.5 && uMode < 8.5) modeAlphaScale = 1.08;
    else if (uMode >= 8.5) modeAlphaScale = 1.04;
    float alphaBase = 0.36 + uIntensity * 0.022 + preEnergy * 0.040;
    if (uMode >= 2.5 && uMode < 3.5) alphaBase = 0.45 + uIntensity * 0.022 + preEnergy * 0.046;
    else if (uMode >= 3.5 && uMode < 5.5) alphaBase = 0.42 + uIntensity * 0.022 + preEnergy * 0.046;
    else if (uMode >= 5.5) alphaBase = 0.38 + uIntensity * 0.020 + preEnergy * 0.044;
    alphaBase *= 1.0 - uPathClarity * (0.12 + uThreadStructure * 0.12 + uPlanarDisk * 0.18 + uSurfaceSheet * 0.08);
    vAlpha = alphaBase * modeVis * modeAlphaScale * clamp(uOpacity, 0.0, 1.0) * uDensityAlphaScale * (0.72 + quietBoost * 0.22);
    vEnergy = preEnergy;
  }
`;

const FRAGMENT_SHADER = `
  precision highp float;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vEnergy;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    if (d > 0.5) discard;

    float core = smoothstep(0.5, 0.0, d);
    core = pow(core, 2.45);
    float halo = exp(-d * d * 16.0) * (0.045 + vEnergy * 0.050);

    float alpha = clamp((core + halo) * vAlpha, 0.0, 0.86);
    if (alpha < 0.01) discard;

    vec3 col = vColor * (0.92 + core * 0.36 + halo * 0.24 + vEnergy * 0.09);
    float peak = max(col.r, max(col.g, col.b));
    col = col / (1.0 + peak * 0.36);
    gl_FragColor = vec4(col, alpha);
  }
`;

export class LiveMathParticleScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 3, g: 4, b: 13, alpha: 0.48, blurPx: 58 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private readonly geometry = new THREE.BufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly points: THREE.Points;
  private readonly family: MathGeometryFamily;
  private readonly structureOverlay: THREE.Group;
  private readonly uniforms: UniformMap = {
    uTime: { value: 0 },
    uCount: { value: 40_000 },
    uMode: { value: 0 },
    uDensityPointScale: { value: 1 },
    uDensityAlphaScale: { value: 1 },
    uPathClarity: { value: 0.5 },
    uPlanarDisk: { value: 0 },
    uSurfaceSheet: { value: 0 },
    uThreadStructure: { value: 0 },
    uPlanetBody: { value: 0 },
    uHue: { value: 0 },
    uIntensity: { value: 1 },
    uExposure: { value: 0.72 },
    uZoomFade: { value: 1 },
    uOpacity: { value: 0.8 },
    uPointSize: { value: 1 },
    uAmplitude: { value: 0.3 },
    uFrequency: { value: 2 },
    uRotation: { value: 0.55 },
    uDepthScale: { value: 0.4 },
    uWarmth: { value: 0.62 },
    uBass: { value: 0 },
    uMid: { value: 0 },
    uTreble: { value: 0 },
    uBeat: { value: 0 },
    uFlow: { value: 1 },
    uQuietBoost: { value: 1 },
  };
  private time = 0;

  constructor(width: number, height: number, mode: LiveMathParticleMode) {
    this.family = familyForMode(mode);
    this.structureOverlay = createStructureOverlay(mode);
    this.camera = new THREE.PerspectiveCamera(48, width / Math.max(1, height), 0.1, 600);
    if (mode === "spiralVortex") {
      this.camera.position.set(0, 28, 55);
    } else if (mode === "fibonacciSequence" || mode === "phyllotacticGalaxy") {
      this.camera.position.set(0, 34, 74);
    } else if (mode === "hopfCathedral" || mode === "knotChoir") {
      this.camera.position.set(0, 22, 76);
    } else if (mode === "songPlanetCore") {
      this.camera.position.set(0, 18, 58);
    } else if (mode === "reactionSkin" || mode === "sphericalHarmonicShell") {
      this.camera.position.set(0, 18, 64);
    } else {
      this.camera.position.set(0, 18, 72);
    }
    this.camera.lookAt(0, 0, 0);

    const positions = new Float32Array(MAX_PARTICLES * 3);
    const indices = new Float32Array(MAX_PARTICLES);
    const seeds = new Float32Array(MAX_PARTICLES * 2);
    for (let index = 0; index < MAX_PARTICLES; index++) {
      indices[index] = index;
      seeds[index * 2] = hashUnit(index + 13);
      seeds[index * 2 + 1] = hashUnit(index + 29);
    }

    this.geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute("aIndex", new THREE.BufferAttribute(indices, 1));
    this.geometry.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 2));
    this.geometry.setDrawRange(0, this.uniforms.uCount.value);
    this.uniforms.uMode.value = MODE_INDEX[mode];
    this.uniforms.uPlanarDisk.value = this.family === "galaxyDisk" ? 1 : 0;
    this.uniforms.uSurfaceSheet.value = this.family === "reactionMembrane" || this.family === "harmonicShell" ? 1 : 0;
    this.uniforms.uThreadStructure.value = this.family === "hopfFibers" || this.family === "knotChoir" ? 1 : 0;
    this.uniforms.uPlanetBody.value = this.family === "songPlanet" ? 1 : 0;

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 2;
    this.scene.add(this.structureOverlay);
    this.scene.add(this.points);
    this.setSize(width, height);
  }

  setSize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.beatLevel = smoothBeat(this.beatLevel, features.beat, dt);
    const level = Math.max(features.level, (features.bass + features.mid + features.treble) / 3);
    this.time += dt * (0.12 + params.flowSpeed * 0.54 + level * 0.42);

    const activeCount = Math.max(
      4_000,
      Math.min(MAX_PARTICLES, qualityCap(params.qualityTier), Math.round(params.mathParticleCount)),
    );
    const density = densitySettingsFor(activeCount, this.family);
    this.geometry.setDrawRange(0, activeCount);

    this.uniforms.uTime.value = this.time;
    this.uniforms.uCount.value = activeCount;
    this.uniforms.uDensityPointScale.value = density.pointScale;
    this.uniforms.uDensityAlphaScale.value = density.alphaScale;
    this.uniforms.uPathClarity.value = density.pathClarity;
    this.uniforms.uHue.value = ((params.paletteHue % 360) + 360) / 360;
    const cameraZoom = THREE.MathUtils.clamp(params.cameraZoom, 0.15, 3.5);
    const zoomFade = THREE.MathUtils.clamp(1 / Math.pow(Math.max(1, cameraZoom), 0.32), 0.82, 1.0);
    const energyFade = THREE.MathUtils.clamp(1 - level * 0.06 - this.beatLevel * 0.035, 0.92, 1.0);
    this.uniforms.uIntensity.value = Math.max(0.65, params.intensity);
    this.uniforms.uExposure.value = THREE.MathUtils.clamp((0.82 + params.intensity * 0.08) * energyFade, 0.72, 1.02);
    this.uniforms.uZoomFade.value = zoomFade;
    this.uniforms.uOpacity.value = THREE.MathUtils.clamp(params.opacity, 0, 1);
    this.uniforms.uPointSize.value = Math.max(0.25, params.mathPointSize);
    this.uniforms.uAmplitude.value = Math.max(0, params.mathAmplitude);
    this.uniforms.uFrequency.value = Math.max(0.2, params.mathFrequency);
    this.uniforms.uRotation.value = Math.max(0, params.mathRotationSpeed);
    this.uniforms.uDepthScale.value = THREE.MathUtils.clamp(params.mathDepthScale, 0, 1.4);
    this.uniforms.uWarmth.value = THREE.MathUtils.clamp(params.mathWarmth, 0, 1);
    this.uniforms.uBass.value = features.bass * params.bassGravity;
    this.uniforms.uMid.value = features.mid * params.midCurl;
    this.uniforms.uTreble.value = features.treble * params.trebleShimmer;
    this.uniforms.uBeat.value = this.beatLevel;
    this.uniforms.uFlow.value = params.flowSpeed;
    this.uniforms.uQuietBoost.value = THREE.MathUtils.clamp(1 - level * 3.25, 0, 1);

    this.structureOverlay.rotation.y += dt * (0.018 + params.mathRotationSpeed * 0.045 + features.mid * 0.012);
    this.structureOverlay.rotation.z += dt * (0.004 + features.treble * 0.01);
    this.structureOverlay.scale.setScalar(1 + this.beatLevel * 0.018 + level * 0.012);
    const overlayLift = THREE.MathUtils.clamp((1.0 + level * 0.10 + this.beatLevel * 0.08) * (0.92 + zoomFade * 0.08), 0.92, 1.14);
    this.structureOverlay.traverse((object) => {
      const material = (object as THREE.Line | THREE.Mesh).material;
      const applyOpacity = (entry: THREE.Material): void => {
        if ("opacity" in entry && typeof entry.userData.baseOpacity === "number") {
          entry.opacity = entry.userData.baseOpacity * overlayLift;
        }
      };
      if (Array.isArray(material)) material.forEach(applyOpacity);
      else if (material) applyOpacity(material);
    });

    const backdropColor = hsl(params.paletteHue + params.mathWarmth * 80, params.saturation, 0.11 + this.beatLevel * 0.08);
    this.backdrop.r = Math.round(backdropColor.r * 70 + features.bass * 25);
    this.backdrop.g = Math.round(backdropColor.g * 58 + features.mid * 35);
    this.backdrop.b = Math.round(backdropColor.b * 90 + features.treble * 35);
    this.backdrop.alpha = 0.42 + level * 0.22;
    this.backdrop.blurPx = 48 + level * 28;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    disposeObjectTree(this.structureOverlay);
    this.scene.clear();
  }
}
